"use client";
import React from 'react';
import { Card, CardVariant, isStockSufficient } from '../../types/card';
import { useTranslations } from 'next-intl';
import { InfoOutlinedIcon } from '../icons';
import { theme as customTheme } from '@/src/theme';
import styles from './StyledComponents.module.css';

interface QuantitySelectorProps {
  product: Card;
  selectedVariant: CardVariant | undefined;
  selectedValue: number | null;
  quantity: number;
  cartQuantityForVariant: number;
  outOfStock: boolean;
  onQuantityChange: (value: number) => void;
}

export const QuantitySelector = React.memo<QuantitySelectorProps>(({
  product,
  selectedVariant,
  selectedValue,
  quantity,
  cartQuantityForVariant,
  outOfStock,
  onQuantityChange,
}) => {
  const t = useTranslations('product');
  // null = the supplier does not count; 0 is already said by the stock badge.
  const stockLeft = selectedVariant?.stock;
  const showStockLeft = !outOfStock && stockLeft != null && stockLeft > 0;

  return (
    <div className={styles.quantitySection}>
      <h3 className={styles.quantitySectionTitle}>
        <InfoOutlinedIcon style={{ color: customTheme.colors.primary, marginRight: 12 }} />
        {t('quantity.title')}
      </h3>
      <div className={styles.quantityControl}>
        <button
          className={styles.quantityButton}
          onClick={() => onQuantityChange(quantity - 1)}
          disabled={quantity <= 1 || outOfStock}
        >−</button>
        <input
          className={styles.quantityInput}
          type="number"
          min="1"
          value={quantity}
          onChange={(e) => onQuantityChange(parseInt(e.target.value) || 1)}
          disabled={outOfStock}
          style={{ opacity: outOfStock ? 0.5 : 1 }}
        />
        <button
          className={styles.quantityButton}
          onClick={() => onQuantityChange(quantity + 1)}
          disabled={
            outOfStock ||
            (selectedValue != null && selectedVariant != null && !isStockSufficient(product, selectedValue, selectedVariant.currency, cartQuantityForVariant + quantity + 1, selectedVariant.countries?.[0]))
          }
        >+</button>
      </div>
      {showStockLeft && (
        <div className={styles.stockLeft}>{stockLeft.toLocaleString()} {t('stock.leftInStock')}</div>
      )}
    </div>
  );
});

QuantitySelector.displayName = 'QuantitySelector';
